import React, { useEffect, useRef } from "react";
import Chart from "chart.js/auto";

const ProgressAndAssessmentScoresChart = ({
  loading = false,
  error = null,
  data = null,
}) => {
  const progressRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    if (!progressRef.current || !data) return;

    // Destroy existing chart if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    if (loading || !data.labels || data.labels.length === 0) {
      return;
    }

    chartInstance.current = new Chart(progressRef.current, {
      type: "bar",
      data: {
        labels: data.labels,
        datasets: [
          {
            label: "Completion Rate",
            data: data.completionRates,
            backgroundColor: "rgba(79,70,229,0.7)",
            borderColor: "rgba(79,70,229,1)",
            borderWidth: 1,
          },
          {
            label: "Assessment Scores",
            data: data.assessmentScores,
            backgroundColor: "rgba(16,185,129,0.7)",
            borderColor: "rgba(16,185,129,1)",
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "top" as const,
          },
          tooltip: {
            callbacks: {
              title: function (context) {
                return `Performance Level: ${context[0].label}`;
              },
              label: function (context) {
                if (context.dataset.label === "Completion Rate") {
                  return `Completion Rate: ${context.parsed.y}%`;
                } else if (context.dataset.label === "Assessment Scores") {
                  return `Assessment Score: ${context.parsed.y}%`;
                }
                return `${context.dataset.label}: ${context.parsed.y}%`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            title: { display: true, text: "Percentage (%)" },
          },
          x: {
            title: {
              display: true,
              text: "Performance Level",
            },
          },
        },
      },
    });

    // Cleanup function
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data, loading]);

  if (loading) {
    return (
      <div className="col-span-full bg-white p-5 border border-gray-200 rounded shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Progress And Assessment Scores
        </h3>
        <div className="h-72 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
            <p className="text-sm text-gray-500">Loading progress data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data || !data.labels || data.labels.length === 0) {
    return (
      <div className="col-span-full bg-white p-5 border border-gray-200 rounded shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Progress And Assessment Scores
        </h3>
        <div className="h-72 flex items-center justify-center">
          <div className="text-gray-500">No progress data available</div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-span-full bg-white p-5 border border-gray-200 rounded shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        Progress And Assessment Scores
      </h3>
      <div className="h-72">
        <canvas ref={progressRef}></canvas>
      </div>
    </div>
  );
};

export default ProgressAndAssessmentScoresChart;
